'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { GpBadge } from '@/components/ui/gp-badge';
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { UserAvatar } from '@/components/ui/user-avatar'
import { Send, SmilePlus, Loader2 } from 'lucide-react'
import { LobbyCodeCard } from './lobby-code'
import { getMyTournamentBan } from '@/lib/tournament-registration'
import { cn } from '@/lib/utils'
import { GifPicker } from './gif-picker'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import {
  getTournamentChatId,
  getActivity,
  isChatMember,
  getLobbyCode,
  postComment,
  toggleReaction,
  type ActivityItem,
} from '@/lib/tournament-chat'

const QUICK_EMOJIS = ['❤️', '😂', '🔥', '👍', '😮']

type JoinGroup = { type: 'join_group'; items: ActivityItem[]; key: string }
type DisplayItem = ActivityItem | JoinGroup

function groupJoins(items: ActivityItem[]): DisplayItem[] {
  const result: DisplayItem[] = []
  let i = 0
  while (i < items.length) {
    if (items[i].type === 'join') {
      const group: ActivityItem[] = [items[i]]
      while (i + 1 < items.length && items[i + 1].type === 'join') {
        i++
        group.push(items[i])
      }
      if (group.length === 1) {
        result.push(group[0])
      } else {
        result.push({ type: 'join_group', items: group, key: group[0].id })
      }
    } else {
      result.push(items[i])
    }
    i++
  }
  return result
}

/** A message whose content is just an image/GIF URL is rendered as media. */
function isMediaUrl(s: string): boolean {
  return /^https?:\/\/\S+\.(gif|gifv|webp|png|jpe?g)(\?\S*)?$/i.test(s.trim())
}

function ago(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

function Avatar({
  url,
  name,
  size = 36,
}: {
  url: string | null | undefined
  name: string
  size?: number
}) {
  return <UserAvatar url={url} alt={name} size={size} className="ring-1 ring-white/10" />
}

/** The lobby code unlocks this many milliseconds before the tournament starts. */
const LOBBY_UNLOCK_LEAD_MS = 5 * 60 * 1000

export function TournamentActivity({
  tournamentId,
  startsAt,
  refreshSignal = 0,
  onLobbyPinnedChange,
  completed = false,
}: {
  tournamentId: string
  startsAt: string
  refreshSignal?: number
  /** Fires true once the lobby-code card scrolls above the viewport. */
  onLobbyPinnedChange?: (pinned: boolean) => void
  /** Hides the lobby code once the tournament has finished. */
  completed?: boolean
}) {
  const { session, user } = useAuth()
  const userId = session?.user?.id ?? null
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'

  const [chatId, setChatId] = useState<string | null>(null)
  const [items, setItems] = useState<ActivityItem[]>([])
  const [isMember, setIsMember] = useState(false)
  const [lobbyCode, setLobbyCode] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [pickerFor, setPickerFor] = useState<string | null>(null)
  const [showGif, setShowGif] = useState(false)
  const [lobbyUnlocked, setLobbyUnlocked] = useState(false)
  const [banned, setBanned] = useState(false)
  // Show the feed 10 at a time; "Load more" reveals the next page.
  const PAGE_SIZE = 10
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  // A host can ban the viewer from this event. RLS then refuses them both the
  // lobby code and any chat post, so resolve the ban once and let the card and
  // the composer explain it rather than failing at the database.
  useEffect(() => {
    if (!userId) {
      setBanned(false)
      return
    }
    let cancelled = false
    getMyTournamentBan(tournamentId, userId).then((ban) => {
      if (!cancelled) setBanned(!!ban)
    })
    return () => {
      cancelled = true
    }
  }, [tournamentId, userId])

  // The lobby code is blurred until 5 minutes before the tournament starts,
  // then reveals on its own — re-checked on a timer so no refresh is needed.
  useEffect(() => {
    const unlockAt = new Date(startsAt).getTime() - LOBBY_UNLOCK_LEAD_MS
    const check = () => {
      const open = Date.now() >= unlockAt
      setLobbyUnlocked(open)
      return open
    }
    if (check()) return
    const id = setInterval(() => {
      if (check()) clearInterval(id)
    }, 30_000)
    return () => clearInterval(id)
  }, [startsAt])

  // The code is only readable (RLS) within the lead window, so fetch it once
  // we're both a member and unlocked. Before then there is nothing to get.
  useEffect(() => {
    if (!isMember || !lobbyUnlocked || banned) {
      setLobbyCode(null)
      return
    }
    let cancelled = false
    ;(async () => {
      const code = await getLobbyCode(tournamentId)
      if (!cancelled) setLobbyCode(code)
    })()
    return () => {
      cancelled = true
    }
  }, [isMember, lobbyUnlocked, banned, tournamentId])

  // Report to the parent when the lobby card scrolls above the viewport, so a
  // sticky copy can be shown above the participants card.
  const lobbyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = lobbyRef.current
    if (!el || !onLobbyPinnedChange) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        onLobbyPinnedChange(!entry.isIntersecting && entry.boundingClientRect.top < 0)
      },
      { rootMargin: '-96px 0px 0px 0px', threshold: 0 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [onLobbyPinnedChange, isMember])

  const refetchActivity = useCallback(
    async (cid: string) => {
      setItems(await getActivity(cid, userId))
    },
    [userId]
  )

  const load = useCallback(async () => {
    const cid = await getTournamentChatId(tournamentId)
    setChatId(cid)
    if (!cid) {
      setItems([])
      setIsMember(false)
      setLobbyCode(null)
      setLoading(false)
      return
    }
    const [acts, member] = await Promise.all([
      getActivity(cid, userId),
      userId ? isChatMember(cid, userId) : Promise.resolve(false),
    ])
    setItems(acts)
    setIsMember(member)
    if (!member) setLobbyCode(null)
    setLoading(false)
  }, [tournamentId, userId])

  useEffect(() => {
    load()
  }, [load, refreshSignal])

  // Realtime: new comments / join entries appear live.
  useEffect(() => {
    if (!chatId) return
    const channel = supabase
      .channel(`tournament_chat:${chatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'group_chat_messages',
          filter: `group_chat_id=eq.${chatId}`,
        },
        () => refetchActivity(chatId)
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [chatId, refetchActivity])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const content = draft.trim()
    if (!content || !chatId || !userId) return
    setPosting(true)
    try {
      await postComment(chatId, userId, content)
      setDraft('')
      await refetchActivity(chatId)
    } catch {
      /* surfaced by RLS if not a member */
    } finally {
      setPosting(false)
    }
  }

  const sendGif = async (url: string) => {
    if (!chatId || !userId) return
    setPosting(true)
    try {
      await postComment(chatId, userId, url)
      await refetchActivity(chatId)
    } catch {
      /* RLS / network */
    } finally {
      setPosting(false)
    }
  }

  const react = async (item: ActivityItem, emoji: string) => {
    if (!userId || !isMember || !chatId) return
    setPickerFor(null)
    const existing = item.reactions.find((r) => r.emoji === emoji)
    const reacted = !!existing?.reactedByMe
    // optimistic
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== item.id) return it
        let reactions = it.reactions.map((r) => ({ ...r }))
        const idx = reactions.findIndex((r) => r.emoji === emoji)
        if (reacted) {
          if (idx >= 0) {
            reactions[idx].count -= 1
            reactions[idx].reactedByMe = false
            if (reactions[idx].count <= 0) reactions = reactions.filter((_, i) => i !== idx)
          }
        } else if (idx >= 0) {
          reactions[idx].count += 1
          reactions[idx].reactedByMe = true
        } else {
          reactions.push({ emoji, count: 1, reactedByMe: true })
        }
        return { ...it, reactions }
      })
    )
    try {
      await toggleReaction(item.id, userId, emoji, reacted)
    } catch {
      refetchActivity(chatId)
    }
  }

  return (
    <section>
      <div className="flex items-baseline gap-2">
        <h2 className="text-lg font-semibold">Activity</h2>
        {!loading && (
          <span className="text-sm text-muted-foreground">
            {items.length} {items.length === 1 ? 'update' : 'updates'}
          </span>
        )}
      </div>

      {/* Lobby code — members only (shows a countdown until it unlocks); hidden once completed */}
      {isMember && !completed && (
        <div ref={lobbyRef}>
          <LobbyCodeCard lobbyCode={lobbyCode} unlocked={lobbyUnlocked} banned={banned} startsAt={startsAt} className="mt-4" />
        </div>
      )}

      {/* Composer */}
      {isMember && banned ? (
        <div className="mt-4 rounded-xl border border-border bg-card/40 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">
            You&apos;ve been banned from this event
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            You can still follow the conversation, but you can&apos;t post here
            or see the lobby code. Reach out to the host if you think this was a
            mistake.
          </p>
        </div>
      ) : isMember ? (
        <form
          onSubmit={submit}
          className="mt-4 flex items-center gap-3 rounded-xl border border-border bg-card/60 px-3 py-2"
        >
          <Avatar url={user?.profile_image_url} name={user?.gamertag ?? '?'} size={32} />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            className="h-9 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={() => setShowGif(true)}
            disabled={posting}
            className="inline-flex h-9 items-center rounded-lg border border-border px-2.5 text-xs font-bold tracking-wide text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground disabled:opacity-40"
            aria-label="Add a GIF"
          >
            GIF
          </button>
          <button
            type="submit"
            disabled={posting || !draft.trim()}
            className="inline-flex size-9 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
            aria-label="Send"
          >
            {posting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          </button>
        </form>
      ) : (
        <div className="mt-4 rounded-xl border border-dashed border-border bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          Join the tournament to get the lobby code and join the conversation.
        </div>
      )}

      {isMember && (
        <GifPicker open={showGif} onClose={() => setShowGif(false)} onSelect={sendGif} />
      )}

      {/* Feed */}
      <div className="mt-6 space-y-5">
        {loading ? (
          <div className="flex justify-center py-8 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-card/40 px-4 py-8 text-center text-sm text-muted-foreground">
            No activity yet — be the first to join and say hi.
          </p>
        ) : (
          groupJoins([...items].reverse().slice(0, visibleCount)).map((display) => {
            if (display.type === 'join_group') {
              const { items: group, key } = display
              const shown = group.slice(0, 3)
              const overflow = group.length - shown.length
              const names = group
                .slice(0, 2)
                .map((it) => it.sender?.gamertag ?? 'Someone')
              const label =
                group.length === 2
                  ? `${names[0]} & ${names[1]}`
                  : overflow > 0
                    ? `${names[0]}, ${names[1]} & ${overflow} others`
                    : names[0]
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="flex shrink-0 items-center">
                    {shown.map((it, idx) => (
                      <span
                        key={it.id}
                        style={{ marginLeft: idx === 0 ? 0 : -10, zIndex: shown.length - idx }}
                        className="relative block rounded-full ring-2 ring-background"
                      >
                        <Avatar url={it.sender?.profile_image_url} name={it.sender?.gamertag ?? '?'} size={36} />
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">{label}</span>{' '}
                    joined the tournament{' '}
                    <span className="text-xs">{ago(group[group.length - 1].createdAt)}</span>
                  </p>
                </div>
              )
            }

            const item = display as ActivityItem
            const name = item.sender?.gamertag ?? 'Someone'
            const profileHref = item.sender?.gamertag
              ? `/${locale}/profile/${encodeURIComponent(item.sender.gamertag)}`
              : null
            return (
              <div key={item.id} className="flex gap-3">
                {profileHref ? (
                  <Link href={profileHref} aria-label={name}>
                    <Avatar url={item.sender?.profile_image_url} name={name} />
                  </Link>
                ) : (
                  <Avatar url={item.sender?.profile_image_url} name={name} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm leading-snug">
                    {profileHref ? (
                      <Link
                        href={profileHref}
                        className="font-semibold underline-offset-2 hover:text-primary hover:underline"
                      >
                        {name}
                      </Link>
                    ) : (
                      <span className="font-semibold">{name}</span>
                    )}
                    <GpBadge userId={item.sender?.id} gamertag={item.sender?.gamertag} size={13} className="ml-1 -mt-0.5" />{' '}
                    {item.type === 'join' ? (
                      <span className="text-muted-foreground">joined the tournament</span>
                    ) : isMediaUrl(item.content) ? (
                      <span className="text-muted-foreground">sent a GIF</span>
                    ) : (
                      <span className="text-foreground/90">{item.content}</span>
                    )}{' '}
                    <span className="text-xs text-muted-foreground">{ago(item.createdAt)}</span>
                  </div>

                  {item.type === 'user' && isMediaUrl(item.content) && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.content}
                      alt=""
                      className="mt-1.5 max-h-64 rounded-lg ring-1 ring-white/10"
                    />
                  )}

                  {/* Reactions */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {item.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        onClick={() => react(item, r.emoji)}
                        disabled={!isMember}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
                          r.reactedByMe
                            ? 'border-primary/50 bg-primary/15 text-foreground'
                            : 'border-border bg-card text-muted-foreground hover:text-foreground',
                          !isMember && 'cursor-default'
                        )}
                      >
                        <span>{r.emoji}</span>
                        <span>{r.count}</span>
                      </button>
                    ))}

                    {isMember && (
                      <div className="relative">
                        <button
                          onClick={() =>
                            setPickerFor((cur) => (cur === item.id ? null : item.id))
                          }
                          className="flex size-7 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
                          aria-label="Add reaction"
                        >
                          <SmilePlus className="size-3.5" />
                        </button>
                        {pickerFor === item.id && (
                          <div className="absolute left-0 top-8 z-20 flex gap-1 rounded-full border border-border bg-card p-1 shadow-xl">
                            {QUICK_EMOJIS.map((e) => (
                              <button
                                key={e}
                                onClick={() => react(item, e)}
                                className="rounded-full px-1.5 py-0.5 text-base transition-transform hover:scale-125"
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}

        {!loading && items.length > visibleCount && (
          <button
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            className="w-full rounded-xl border border-border bg-card py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
          >
            Load more ({items.length - visibleCount} older)
          </button>
        )}
      </div>
    </section>
  )
}
